/**
 * Enrollment & Census (Screen J) — PARTIAL/caveated enrollment insight. Two SkyCard sections, each
 * badged from its OWN meta.provenance via ProvenanceBadge (never hand-tagged):
 *   1. Census (interim COUNT(op_dim_member); op_fact_member_month empty) — the count is shown WITH a
 *      mandatory roster-pending, non-PACE-validated caveat so it never reads as an authoritative silent
 *      number.
 *   2. Cohort coverage health (expected_census / matched / match_pct / signal_status) — the honest
 *      "enrollment %" insight, NOT a fabricated funnel.
 * Each section isolates its own loading/error; an empty section reads from the badge, never a silent zero.
 */
import { SkyCard, SkyText, SkyLoader, SkyMessageBar, SkyDataGrid, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import type {
  Census as CensusData,
  CohortHealth,
  CoveragePlanTypeSummary,
  CoveragePlanType,
} from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { COPY, MICROCOPY } from "../lib/copy";
import { deriveBadge } from "../provenance/deriveBadge";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

const ROSTER_PENDING_CAVEAT =
  "Roster-pending: the member-month feed has not landed, so this is a non-PACE-validated denominator, not an authoritative census.";

// Cohort-diagnostic caveat — kept WORD-FOR-WORD consistent with the Care Gaps non-cohort caveat so the
// honesty story reads identically across the app. The closing line names the concrete consequence this
// tile is the evidence for: with 0 PACE-confirmed members, int_pace_cohort yields no member-months, so
// every PACE per-member-month rate is NULL by design.
const NON_COHORT_CAVEAT =
  "These are internal indicators, not CMS PACE/HPMS measures and not MA Star Ratings. The sandbox is " +
  "generic eCW FHIR data (0 of 2299 members PACE-confirmed), so every rate sits on a NON-PACE " +
  "denominator: treat it as a signal, not an authoritative PACE cohort. Because no member carries a PACE " +
  "(all-inclusive-care) coverage plan type, PACE per-member-month rates are NULL by design until a real " +
  "ElementCare roster lands.";

function Metric({ value, caption }: { value: string; caption: string }) {
  return (
    <div className="flex flex-col">
      <SkyText size={700} weight="bold">
        {value}
      </SkyText>
      <SkyText size={200}>{caption}</SkyText>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <SkyText size={200}>{label}</SkyText>
      <SkyText size={300} weight="medium">
        {value}
      </SkyText>
    </div>
  );
}

function CensusTile() {
  const { data, meta, loading, error, retry } = usePage<CensusData>("/api/v1/census");
  return (
    <SkyCard
      header={{
        title: "Center census",
        description: "Interim active-participant count",
        action: <ProvenanceBadge meta={meta?.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading census" />}
      {error && <PageError title="Could not load the census" onRetry={retry} />}
      {!loading && !error && (
        <div className="flex flex-col gap-3">
          <Metric
            value={data ? String(data.count) : MICROCOPY.awaitingFeed}
            caption={data?.month ? `as of ${data.month}` : "Month not recorded"}
          />
          <SkyText size={200}>{ROSTER_PENDING_CAVEAT}</SkyText>
        </div>
      )}
    </SkyCard>
  );
}

function CohortHealthBand() {
  const { data, meta, loading, error, retry } = usePage<CohortHealth>("/api/v1/census/cohort-health");
  return (
    <SkyCard
      header={{
        title: "Cohort coverage health",
        description: "Enrollment coverage insight (matched roster vs expected census)",
        action: <ProvenanceBadge meta={meta?.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading cohort health" />}
      {error && <PageError title="Could not load cohort health" onRetry={retry} />}
      {!loading && !error && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
            <Field label="Expected census" value={data ? String(data.expected_census) : MICROCOPY.awaitingFeed} />
            <Field
              label="Matched members"
              value={data ? String(data.coverage_matched_members) : MICROCOPY.awaitingFeed}
            />
            <Field
              label="Match %"
              value={data ? `${data.match_pct}%` : MICROCOPY.awaitingFeed}
            />
            <Field label="Signal" value={data?.signal_status ?? MICROCOPY.awaitingFeed} />
          </div>
          <SkyText size={200}>
            This is the honest enrollment-coverage percentage, not a fabricated acquisition funnel.
          </SkyText>
        </div>
      )}
    </SkyCard>
  );
}

const planTypeColumns: SkyDataGridColumn<CoveragePlanType>[] = [
  {
    key: "plan_type_code",
    header: "Plan type (coverage)",
    width: "60%",
    renderCell: (_v, row) => row.plan_type_code ?? MICROCOPY.fieldAbsent,
  },
  { key: "member_count", header: "Members", width: "40%", align: "right" },
];

/**
 * Coverage plan-type & PACE-cohort diagnostic (A7) — the BLOCKER #1 evidence card. Reads the REAL
 * gold.dim_coverage plan_type distribution (current coverage only) and reports the HONEST PACE-confirmed
 * member count against the FULL roster (op_dim_member) — the "0 of 2299" verdict every non-cohort caveat
 * across the app points back to. Because plan_type_code in this sandbox is {Commercial, Self-Pay, Other}
 * with NO PACE value, pace_confirmed is 0 and the non-cohort caveat STANDS (surfaced as evidence, never
 * softened into a fabricated PACE cohort).
 */
function CoveragePlanTypeTile() {
  const { data, meta, loading, error, retry } = usePage<CoveragePlanTypeSummary>(
    "/api/v1/census/coverage-plan-types",
  );
  const paceConfirmed = data?.pace_confirmed_members ?? 0;
  const rosterTotal = data?.roster_total ?? 0;
  const withCoverage = data?.members_with_coverage ?? 0;
  // H2 provenance gate (mirror Dashboard DecisionTile.showMetric): the "PACE-confirmed: N of M" metric +
  // the "Coverage cannot confirm PACE enrollment" conclusion render ONLY when the derived tier is
  // REAL/PARTIAL. A DEFERRED/MOCK feed (e.g. dim_coverage absent -> deferredProvenance, 0/0) shows the
  // shared "Awaiting feed" holding state instead of a false "PACE-confirmed: 0 of 0" + false verdict.
  const tier = deriveBadge(meta?.provenance).tier;
  const showMetric = tier === "REAL" || tier === "PARTIAL";
  return (
    <SkyCard
      header={{
        title: "PACE-cohort diagnostic",
        description: "Does coverage identify the PACE cohort? (gold.dim_coverage plan_type_code)",
        action: <ProvenanceBadge meta={meta?.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading coverage plan types" />}
      {error && <PageError title="Could not load coverage plan types" onRetry={retry} />}
      {!loading && !error && !showMetric && (
        <SkyText size={300}>{COPY.awaitingFeedHolding}</SkyText>
      )}
      {!loading && !error && showMetric && data && (
        <div className="flex flex-col gap-3">
          {/* P2 reframe — lead with the interim ROSTER count as the headline number so the big figure
              reads as "who is on the roster", never as members lost. The PACE-confirmed tally then reads
              as a DATA-CONFIRMATION diagnostic against that denominator (0 = "not yet confirmable",
              pending the member-month feed), not a catastrophic enrollment loss. */}
          <Metric
            value={String(rosterTotal)}
            caption="members on the interim roster (gold-derived); the PACE cohort is confirmed against this denominator below"
          />
          <SkyText size={300}>
            {`PACE-confirmed via coverage plan type: ${paceConfirmed} of ${rosterTotal} members (${withCoverage} have a current coverage record on file).`}
          </SkyText>
          {paceConfirmed === 0 ? (
            <SkyMessageBar
              intent="warning"
              title="PACE enrollment not yet confirmable from coverage"
              message="Coverage cannot yet confirm PACE enrollment for any member - pending the member-month feed. This is a data-confirmation gap, not a loss of members: the interim roster above still stands. The current plan types present are listed below, so the non-cohort denominator caveat holds: this remains a general FHIR population, not a verified PACE cohort."
            />
          ) : (
            <SkyText size={200}>
              {paceConfirmed} member(s) have a PACE-typed coverage plan. This partially confirms the PACE
              cohort for those members; the remainder stay non-cohort until verified.
            </SkyText>
          )}
          <SkyDataGrid<CoveragePlanType>
            data={data.plan_types}
            columns={planTypeColumns}
            getRowId={(r, i) => r.plan_type_code ?? String(i)}
            emptyMessage="No current coverage plan types on record"
          />
          {/* P1-2: the structural non-cohort caveat (same wording as Care Gaps) is the calmer info tier;
              the elevated "cannot confirm PACE" finding above carries the safety-critical salience. */}
          <SkyMessageBar
            intent="info"
            title="Internal indicators on a non-PACE denominator"
            message={NON_COHORT_CAVEAT}
          />
          <SkyText size={200}>
            Current plan types are read from gold.dim_coverage via the member bridge. This is the honest
            evidence for the PACE-cohort question, not a roster of enrolled PACE participants.
          </SkyText>
        </div>
      )}
    </SkyCard>
  );
}

export function Census() {
  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "census", label: "Census" }]}
        title="Enrollment & Census"
        description="The honest view of who is enrolled now: an interim, roster-pending census plus a coverage check of whether the PACE cohort can be confirmed."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <CensusTile />
        <CoveragePlanTypeTile />
        <CohortHealthBand />
      </section>
      <PageFooter />
    </>
  );
}
